import React, { useEffect, useMemo, useRef, useState } from 'react';
import Background from '../components/tvs/Background';
import VideoStrip from '../components/tvs/VideoStrip';

import GoogleSlidesViewer from '../components/tvs/GoogleSlidesViewer';

const IDLE_HIDE_MS = 4000;

export default function TVsPage() {
  const [visibleUI, setVisibleUI] = useState(true);
  const [name, setName] = useState(() => localStorage.getItem('welcomeName') || 'Duluth Dental Center');

  const [safeMode, setSafeMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSlides, setShowSlides] = useState(false);
  const [slideCount, setSlideCount] = useState(() => parseInt(localStorage.getItem('slideCount')) || 6);
  const fsRef = useRef(null);
  const idleTimer = useRef(null);
  const slidesTimerRef = useRef(null);
  const manualSlidesTimer = useRef(null);

  const poke = () => {
    setVisibleUI(true);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setVisibleUI(false), IDLE_HIDE_MS);
  };

  useEffect(() => {
    const onMove = () => poke();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mousedown', onMove);
    window.addEventListener('touchstart', onMove);
    poke();
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mousedown', onMove);
      window.removeEventListener('touchstart', onMove);
      clearTimeout(idleTimer.current);
    };
  }, []);

  useEffect(() => {
    const val = (name || '').slice(0, 64).trim();
    localStorage.setItem('welcomeName', val || 'Duluth Dental Center');
  }, [name]);

  useEffect(() => {
    localStorage.setItem('slideCount', slideCount.toString());
  }, [slideCount]);

  // Detect Windows and/or reduced motion and enable safe mode to reduce GPU load
  useEffect(() => {
    try {
      const ua = navigator.userAgent || '';
      const isWindows = /Windows/i.test(ua);
      const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const params = new URLSearchParams(window.location.search);
      const paramSafe = params.has('safe') ? params.get('safe') : null;
      const stored = localStorage.getItem('tvsSafeMode');

      let enable = isWindows || prefersReduced;
      if (paramSafe !== null) enable = paramSafe === '1' || paramSafe === 'true';
      if (stored !== null) enable = stored === '1' || stored === 'true';
      setSafeMode(enable);
    } catch (e) {
      setSafeMode(false);
    }
  }, []);

  // Fullscreen change listener
  useEffect(() => {
    const onFsChange = () => {
      const el = document.fullscreenElement;
      setIsFullscreen(!!el && fsRef.current && el === fsRef.current);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (!fsRef.current) return;
    if (!document.fullscreenElement) {
      fsRef.current.requestFullscreen?.();
    } else if (document.fullscreenElement === fsRef.current) {
      document.exitFullscreen?.();
    } else {
      // If another element is fullscreen, exit then request on ours
      document.exitFullscreen?.().then(() => fsRef.current.requestFullscreen?.());
    }
  };

  // Google Slides cycle: Every 10 minutes
  useEffect(() => {
    const showSlidesOnCycle = () => {
      setShowSlides(true);

      const slidesDuration = slideCount * 30 * 1000;

      slidesTimerRef.current = setTimeout(() => {
        setShowSlides(false);
      }, slidesDuration);
    };

    showSlidesOnCycle();
    const slidesInterval = setInterval(showSlidesOnCycle, 10 * 60 * 1000);

    return () => {
      clearInterval(slidesInterval);
      if (slidesTimerRef.current) {
        clearTimeout(slidesTimerRef.current);
      }
    };
  }, [slideCount]);


  const handleShowSlides = () => {
    setShowSlides(true);

    // Clear any existing manual timer
    if (manualSlidesTimer.current) {
      clearTimeout(manualSlidesTimer.current);
    }

    // Auto-close after slideCount * 30 seconds
    manualSlidesTimer.current = setTimeout(() => {
      setShowSlides(false);
    }, slideCount * 30 * 1000);
  };

  const handleCloseSlides = () => {
    setShowSlides(false);
    // Clear the auto-close timer when manually closed
    if (manualSlidesTimer.current) {
      clearTimeout(manualSlidesTimer.current);
    }
  };

  const toolbarStyle = useMemo(() => ({
    position: 'fixed',
    top: 16,
    left: 16,
    padding: '10px 12px',
    borderRadius: 12,
    background: safeMode ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.45)',
    color: 'white',
    display: visibleUI ? 'flex' : 'none',
    alignItems: 'center',
    gap: 8,
    zIndex: 30,
    ...(safeMode ? {} : { backdropFilter: 'blur(6px)' })
  }), [visibleUI, safeMode]);

  return (
    <div ref={fsRef} style={{ width: '100%', height: '100%' }}>
      <Background reducedMotion={safeMode} />

      <div style={toolbarStyle}>
        <input
          aria-label="Your name"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Duluth Dental Center"
          style={{
            padding: '8px 10px',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.25)',
            background: 'rgba(255,255,255,0.1)',
            color: 'white',
            outline: 'none',
            width: 180
          }}
        />
        <input
          type="number"
          min="1"
          max="20"
          value={slideCount}
          onChange={e => setSlideCount(Math.max(1, parseInt(e.target.value) || 1))}
          aria-label="Number of slides"
          style={{
            padding: '8px 10px',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.25)',
            background: 'rgba(255,255,255,0.1)',
            color: 'white',
            outline: 'none',
            width: 60,
            textAlign: 'center'
          }}
        />
        <button
          onClick={toggleFullscreen}
          style={{
            padding: '8px 10px',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.25)',
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        </button>
        <button
          onClick={handleShowSlides}
          style={{
            padding: '8px 10px',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.25)',
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Patient Slides
        </button>
      </div>

      {showSlides && <GoogleSlidesViewer onClose={handleCloseSlides} />}
      <VideoStrip />
    </div>
  );
}
